import type {
  CreateFlowRequest,
  FlowContent,
  FlowEditorView,
  FlowPatientOverview,
  FlowSummary,
  UpsertFlowDraft,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { jsonOr401 } from "./clinician-api";

// TES-160 — flow authoring client for the admin day-grid builder. Auth rides on
// the httpOnly clinician session cookie (`withCredentials`), scoped by
// `tenantHeader()`, exactly like the intake template editor (intake-template-api.ts).

function commonHeaders(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    ...tenantHeader(),
  };
}

// Backend validation (400) + conflict (409) bodies come back as JSON; surface
// their message verbatim so the builder shows the actual rule that tripped.
export class FlowApiError extends Error {}

async function jsonOrError<T>(res: Response): Promise<T> {
  if (res.status === 401 || res.status === 403) {
    return jsonOr401<T>(res);
  }
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: unknown; error?: unknown };
      const msg = body.message ?? body.error;
      if (typeof msg === "string" && msg.trim()) detail = msg;
      else if (Array.isArray(msg) && msg.length) detail = msg.join("; ");
    } catch {
      // non-JSON body — keep the status-code detail
    }
    throw new FlowApiError(detail);
  }
  return (await res.json()) as T;
}

export async function listFlows(): Promise<FlowSummary[]> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/flows`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function fetchFlow(key: string): Promise<FlowEditorView> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/flows/${key}`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function createFlow(
  body: CreateFlowRequest,
): Promise<FlowContent> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/flows`, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function saveFlowDraft(
  key: string,
  body: UpsertFlowDraft,
): Promise<FlowContent> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/flows/${key}/draft`, {
    method: "PUT",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function publishFlow(key: string): Promise<FlowContent> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/flows/${key}/publish`,
    {
      method: "POST",
      headers: commonHeaders(),
      ...withCredentials,
    },
  );
  return jsonOrError(res);
}

export async function archiveFlow(key: string): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/flows/${key}/archive`,
    {
      method: "POST",
      headers: commonHeaders(),
      ...withCredentials,
    },
  );
  await expectNoContent(res);
}

// --- TES-164/166: per-patient timeline + assignment ------------------------

async function expectNoContent(res: Response): Promise<void> {
  if (res.status === 401 || res.status === 403) {
    await jsonOr401<void>(res);
    return;
  }
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: unknown; error?: unknown };
      const msg = body.message ?? body.error;
      if (typeof msg === "string" && msg.trim()) detail = msg;
    } catch {
      // keep the status-code detail
    }
    throw new FlowApiError(detail);
  }
}

export async function fetchPatientFlows(
  patientId: string,
): Promise<FlowPatientOverview> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/flows`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOrError(res);
}

// Assign the latest published version of a flow (by key) to a patient.
// TES-288: `reason` (why the clinician starts it) is persisted + audited;
// `expectedVersion` pins the assign to the previewed version (409 on stale).
export async function assignFlowToPatient(
  patientId: string,
  flowKey: string,
  reason?: string,
  expectedVersion?: number,
): Promise<{ id: string; status: string; created: boolean }> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/flows`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify({
        flowKey,
        reason: reason || undefined,
        expectedVersion,
      }),
      ...withCredentials,
    },
  );
  return jsonOrError(res);
}

// TES-288 — resumable suspension of an assignment.
export async function pausePatientFlow(
  patientId: string,
  assignmentId: string,
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/flows/${assignmentId}/pause`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  await expectNoContent(res);
}

export async function resumePatientFlow(
  patientId: string,
  assignmentId: string,
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/flows/${assignmentId}/resume`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  await expectNoContent(res);
}

export async function removePatientFlowItem(
  patientId: string,
  itemId: string,
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/flows/items/${itemId}`,
    { method: "DELETE", headers: commonHeaders(), ...withCredentials },
  );
  await expectNoContent(res);
}

export async function cancelPatientFlow(
  patientId: string,
  assignmentId: string,
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/flows/${assignmentId}`,
    { method: "DELETE", headers: commonHeaders(), ...withCredentials },
  );
  await expectNoContent(res);
}
